(ns ui.components.input.checkbox
  (:require
   ["lucide-react" :refer [Check]]
   [common.util :as cu]
   [ui.util :as uu]))

;; the fixed tw2-class-names until we get styles.json from zapkit-components.
;; Transcribed from zapkit-components/src/components/Checkbox/Checkbox.styles.ts
;; and Checkbox.tsx.
;;
;; This map is deliberately a FAITHFUL mirror of upstream, including the three
;; entries we do not read. Keep it that way: when the real styles.json lands,
;; rebinding `styles` to a generated `zapkit-styles/checkbox` should be an
;; overwrite, not a reconciliation. Our deviations live in `tw2-checkbox`
;; instead, expressed as *which keys it reads* and *what it appends* — never as
;; edits to the strings here.
;;
;; The unread entries are the ones that assume upstream's DOM, which is not
;; ours:
;;   upstream  <label> > input.sr-only.peer + span.box > span.checkmark
;;   ours      div.box > input(transparent, on top) + span.checkmark
;; Zapflow labels inputs from the outside — ui.editors.labelled-input renders a
;; sibling <label> with no `for` — so nothing here can take the click on behalf
;; of an `sr-only` input. The input has to remain the click target itself.
;;
;; Everything else transfers verbatim, and not by luck: upstream's `hover:` and
;; `active:` still match the box as an ancestor of the hovered input, and
;; upstream drives its checkmark from a state prop rather than `peer-checked`.
(def temp-styles
  {:checkbox
   {:base
    "tw2-relative tw2-inline-flex tw2-items-center tw2-justify-center tw2-w-4 tw2-h-4 tw2-rounded-checkbox tw2-border tw2-shadow-xs tw2-transition-all tw2-duration-150 tw2-ease-in-out tw2-cursor-pointer hover:tw2-shadow-sm active:tw2-shadow-xs"
    :state
    {:unchecked
     "tw2-bg-checkbox-bg-default tw2-border-checkbox-border-default hover:tw2-bg-checkbox-bg-hover hover:tw2-border-checkbox-border-hover active:tw2-bg-checkbox-bg-pressed active:tw2-border-checkbox-border-pressed"
     :checked
     "tw2-bg-checkbox-bg-selected tw2-border-checkbox-border-selected hover:tw2-bg-checkbox-bg-selected-hover hover:tw2-border-checkbox-border-selected-hover active:tw2-bg-checkbox-bg-selected-pressed active:tw2-border-checkbox-border-selected-pressed"
     :disabled
     "tw2-bg-checkbox-bg-disabled tw2-border-checkbox-border-disabled tw2-shadow-none tw2-cursor-not-allowed tw2-opacity-50"}}
   :checkmark
   {:base
    "tw2-absolute tw2-inset-0 tw2-flex tw2-items-center tw2-justify-center tw2-text-checkbox-checkmark-bg tw2-text-xs tw2-font-semibold"
    :state
    {:unchecked "tw2-opacity-0"
     :checked "tw2-opacity-100"
     :disabled "tw2-opacity-50"}}
   :icon
   {:base "tw2-w-[10px] tw2-h-[10px]"}
   ;; Not read: upstream hides the input and takes clicks on its <label>.
   :input
   {:base "tw2-sr-only tw2-peer"}
   ;; Not read: `peer-*` compiles to a sibling selector and our input is the
   ;; box's child, so these can never match.
   :focus-ring
   {:base
    "peer-focus-visible:tw2-outline-none peer-focus-visible:tw2-ring-2 peer-focus-visible:tw2-ring-offset-2"
    :unchecked "peer-focus-visible:tw2-ring-checkbox-border-default"
    :checked "peer-focus-visible:tw2-ring-checkbox-border-selected"}
   ;; Not read: labels come from ui.editors.labelled-input.
   :label
   {:base "tw2-flex tw2-items-center tw2-gap-checkbox-gap tw2-cursor-pointer"}})

;; What `tw2-checkbox` uses in place of the unread `temp-styles` entries, kept
;; out of that map so a regenerated one can overwrite it wholesale.
(def dom-overrides
  {;; for [:input :base] — stretched over the box and transparent instead of
   ;; `sr-only`, so it stays what the pointer and the focus ring land on.
   :input
   "tw2-absolute tw2-inset-0 tw2-w-full tw2-h-full tw2-m-0 tw2-opacity-0 tw2-cursor-pointer disabled:tw2-cursor-not-allowed"
   ;; for [:focus-ring *] — `has-` looks down into the subtree, where our
   ;; input is.
   :focus-ring
   {:base
    "has-[:focus-visible]:tw2-outline-none has-[:focus-visible]:tw2-ring-2 has-[:focus-visible]:tw2-ring-offset-2"
    :unchecked "has-[:focus-visible]:tw2-ring-checkbox-border-default"
    :checked "has-[:focus-visible]:tw2-ring-checkbox-border-selected"}
   ;; appended to [:checkmark :base] — it covers the input, and would otherwise
   ;; swallow the clicks.
   :checkmark "tw2-pointer-events-none tw2-transition-opacity"
   ;; TEMPORARY, appended to the checkmark when disabled *and* checked.
   ;; Upstream's `state` is one 3-way value, so `disabled` replaces `checked`
   ;; outright: the box gets --checkbox-bg-disabled (gray-50 in light theme)
   ;; while the checkmark keeps --checkbox-checkmark-bg (white), leaving a
   ;; disabled checked box looking empty. Reproduces in upstream's own
   ;; Storybook; only light theme is affected, dark theme's disabled background
   ;; is gray-700 and the white tick survives on it.
   ;; `text-primary` is readable on the disabled background in both themes.
   ;; Remove once upstream defines a disabled+checked appearance.
   :checkmark-disabled-checked "tw2-text-primary"
   ;; appended when disabled — the disabled state resolves `shadow-xs` and
   ;; `cursor-pointer` out of the base, but the base's hover/active shadows are
   ;; a different variant group and survive.
   :disabled "hover:tw2-shadow-none active:tw2-shadow-none"})

(defn checkbox-state
  "Which `[:checkbox :state]` key a checkbox renders in."
  [{:keys [checked? disabled?]}]
  (cond
    disabled? :disabled
    checked? :checked
    :else :unchecked))

(defn input-attrs
  "Attributes for the native input. Shared by both variants — they differ in
   how the input is presented, not in what it does."
  [{:keys [id checked? disabled? on-change on-focus on-blur class]}]
  (merge
   {:class     class
    :type      "checkbox"
    :value     checked?
    :checked   checked?
    :disabled  (boolean disabled?)
    :on-focus  (fn [_e]
                 (when (fn? on-focus)
                   (on-focus)))
    :on-blur   (fn [_e]
                 (when (fn? on-blur)
                   (on-blur checked?)))
    :on-change (fn [_e]
                 (when (fn? on-change)
                   (on-change (not checked?))))}
   (when id
     {:id id})))

(defn legacy-checkbox
  "The original checkbox: the native input is the visible box, styled directly
   with `appearance-none`, and the checkmark is an overlay driven by
   `peer-checked`."
  [{:keys [disabled? class wrapper-class input-class indicator-class
           icon-class checked-icon]
    :as props}]
  (let [icon-classes (uu/cm "h-4 w-4 text-input-gloomy-text"
                            icon-class)]
    [:div
     {:class (uu/cm "w-4 relative inline-flex cursor-pointer items-center rounded"
                    wrapper-class
                    class)}
     [:input
      (input-attrs
       (assoc props
              :class (uu/cm "peer appearance-none relative h-4 w-4 bg-input-light border border-input-light-border-light transition-all checked:border-input-gloomy checked:bg-input-gloomy rounded"
                            (if disabled?
                              "cursor-not-allowed"
                              "cursor-pointer")
                            input-class)))]
     [:div
      {:class (uu/cm "flex items-center pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-input-gloomy-text opacity-0 transition-opacity peer-checked:opacity-100"
                     indicator-class)
       :aria-hidden true}
      (or checked-icon
          [:svg
           {:class icon-classes
            :xmlns "http://www.w3.org/2000/svg"
            :fill "currentColor"
            :viewBox "0 0 20 20"
            :stroke "currentColor"
            :stroke-width "1"}
           [:path
            {:fill-rule "evenodd"
             :clip-rule "evenodd"
             :d "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"}]])]]))

(defn tw2-checkbox
  "The ZapKit checkbox: the wrapper is the painted box and the native input
   sits on top of it, stretched and transparent.

   Reads `temp-styles` for everything upstream can give us and `dom-overrides`
   for the rest. Three deviations, all of them forced by the input being the
   click target rather than a <label>:

   - the input is overlaid instead of `sr-only`
   - the focus ring uses `has-` rather than `peer-`
   - the checkmark is `pointer-events-none`, or it eats the clicks

   plus one correction: the checkmark state is looked up by checked-ness, not
   by the box's state, so an unchecked disabled box does not show a ghost tick
   through upstream's `:disabled` opacity."
  [{:keys [checked? disabled? class wrapper-class input-class indicator-class
           icon-class checked-icon]
    :as props}]
  (let [styles temp-styles
        state (checkbox-state {:checked? checked?
                               :disabled? disabled?})
        checkmark-state (if checked?
                          :checked
                          :unchecked)]
    [:div
     {:class (uu/cm (get-in styles [:checkbox :base])
                    (get-in styles [:checkbox :state state])
                    (get-in dom-overrides [:focus-ring :base])
                    (get-in dom-overrides [:focus-ring state])
                    (when disabled?
                      (:disabled dom-overrides))
                    wrapper-class
                    class)}
     [:input
      (input-attrs
       (assoc props
              :class (uu/cm (:input dom-overrides)
                            input-class)))]
     [:span
      {:class (uu/cm (get-in styles [:checkmark :base])
                     (get-in styles [:checkmark :state checkmark-state])
                     (:checkmark dom-overrides)
                     (when (and disabled? checked?)
                       (:checkmark-disabled-checked dom-overrides))
                     indicator-class)
       :aria-hidden true}
      (or checked-icon
          [:> Check
           {:className (uu/cm (get-in styles [:icon :base])
                              icon-class)
            :aria-hidden true}])]]))

(defn view
  [{:keys [value style-variant]
    :or {style-variant :legacy}
    :as props}]
  (let [checked? (cond (boolean? value)
                       value
                       (string? value)
                       (not (cu/blank-value? value))
                       :else (boolean value))
        props (assoc props :checked? checked?)]
    (case style-variant
      :tw2 [tw2-checkbox props]
      [legacy-checkbox props])))
